import { useReducer } from 'react';

import { NestedLocation, NestedLocationIndex } from '../../../types/nestedLocations';
import {
  changeAncestorsSelectionStatus,
  clearChildrenSelectStatus,
  convertSelectedLocationsToMap,
  getLocationAncestors,
  unselectLocationAncestors,
} from './LocationsHelpers';

export type SelectedStatus =
  | 'selected'
  | 'excluded'
  | 'onlyChildren'
  /** Group mode: parent kept after "deselect all" children — UI shows indeterminate [-]. */
  | 'partialChildren'
  | undefined;
export type SelectedLocations = Map<number, SelectedStatus>;

export interface LocationsState {
  locationById: NestedLocationIndex;
  childrenByParentId: Map<number, number[]>;
  selectedLocations: SelectedLocations;
  allLocationsSelected: boolean;
}

interface ResetLocationStateAction {
  type: 'reset_location_state';
  payload: Partial<LocationsState>;
}

interface UpdateLocationChildrenAction {
  type: 'update_location_children';
  payload: {
    targetLocationId: number;
    newLocations: NestedLocation[];
    has_more_children: boolean;
    /**
     * Offset the batch was asked for. Zero replaces this parent's children, anything
     * else appends. The condition is load-bearing: "load more" uses this same action
     * with an accumulated offset, so an unconditional replace would leave the tree
     * showing only the newest batch every time.
     *
     * Required, not optional. It selects between two opposite behaviours, so a future
     * dispatcher that forgot it would silently take the append branch, which is the very
     * duplication this condition exists to prevent.
     */
    offset: number;
  };
}

interface SetLocationExpandedAction {
  type: 'set_location_expanded';
  payload: {
    locationId: number;
    /**
     * Target state. Omitted means toggle, which is what a chevron click wants.
     *
     * The reopen pass passes `true` explicitly, and that distinction is the whole point:
     * two independent writers both meaning "make it open" cannot share a toggle. When a
     * pass and a click, or two overlapping passes, both toggle the same node, the second
     * one closes what the first opened.
     */
    expanded?: boolean;
  };
}

interface UpdateSignleLocationSelectionAction {
  type: 'update_single_location_selection';
  payload: number;
}

interface UpdateLocationSelectionAction {
  type: 'update_location_selection';
  payload: number;
}

interface ToggleAllLocationsSelectionAction {
  type: 'toggle_all_locations_selection';
}

interface DeleteLocationAction {
  type: 'delete_location';
  payload: {
    locationId: number;
  };
}

type LocationsAction =
  | ResetLocationStateAction
  | UpdateLocationChildrenAction
  | SetLocationExpandedAction
  | UpdateLocationSelectionAction
  | UpdateSignleLocationSelectionAction
  | ToggleAllLocationsSelectionAction
  | DeleteLocationAction;

export const getDefaultLocationsState = (
  initialSelectedLocations?: Record<string, SelectedStatus>,
  initialAllLocationsSelected?: boolean,
): LocationsState => ({
  locationById: new Map<number, NestedLocation>(),
  selectedLocations: convertSelectedLocationsToMap(initialSelectedLocations || {}),
  childrenByParentId: new Map<number, number[]>(),
  allLocationsSelected: initialAllLocationsSelected || false,
});

/**
 * Toggles the selection status of a specific location
 * At the same time it change the selection status of the location and its ancestors or parents.
 * It also clear the selection status of all children of the location.
 */
const toggleLocationSelectionAction = (
  state: LocationsState,
  { payload: locationId }: UpdateLocationSelectionAction,
) => {
  const newState = {
    locationById: new Map(state.locationById),
    childrenByParentId: new Map(state.childrenByParentId),
    selectedLocations: new Map(state.selectedLocations),
    allLocationsSelected: state.allLocationsSelected,
  };

  const location = newState.locationById.get(locationId);
  if (!location) return state;

  const ancestors = getLocationAncestors(location.id, newState.locationById);
  const locationSelectedStatus = newState.selectedLocations.get(location.id);

  const atLeastOneAncestorSelected = ancestors.some((ancestor) =>
    ['selected', 'onlyChildren'].includes(newState.selectedLocations.get(ancestor.id)),
  );
  const noAncestorExcluded = ancestors.every(
    (ancestor) => newState.selectedLocations.get(ancestor.id) !== 'excluded',
  );

  const isSelected =
    (!['excluded', 'onlyChildren'].includes(locationSelectedStatus) &&
      atLeastOneAncestorSelected &&
      noAncestorExcluded) ||
    locationSelectedStatus === 'selected' ||
    state.allLocationsSelected;

  clearChildrenSelectStatus(
    location.id,
    newState.locationById,
    newState.childrenByParentId,
    newState.selectedLocations,
  );

  if (isSelected) {
    newState.selectedLocations.set(location.id, 'excluded');
    unselectLocationAncestors(location.id, newState.selectedLocations, newState.locationById);
    newState.allLocationsSelected = false;
  } else {
    newState.selectedLocations.set(location.id, 'selected');
    changeAncestorsSelectionStatus(
      location.parent_id,
      newState.selectedLocations,
      newState.locationById,
      newState.childrenByParentId,
    );
  }

  return newState;
};

/**
 * Add a batch of sublocations to the target location, and update its has_more_children.
 *
 * A batch asked for at offset 0 REPLACES the parent's children rather than appending to
 * them. Without that, a reordered first batch lands on top of the previous order's rows
 * and the branch shows both sequences at once.
 */
const updateLocationChildrenAction = (
  state: LocationsState,
  action: UpdateLocationChildrenAction,
) => {
  const locationById = new Map(state.locationById);
  const childrenByParentId = new Map(state.childrenByParentId);

  // Get the target location
  const targetLocation = locationById.get(action.payload.targetLocationId);
  if (!targetLocation) return state;

  // Update the target location with the new has_more_children value
  targetLocation.has_more_children = action.payload.has_more_children;

  if (action.payload.offset === 0) {
    childrenByParentId.set(action.payload.targetLocationId, []);
  }

  // Add new locations to the locationById and the childrenByParentId.
  //
  // `expanded` is carried over from the row being replaced. It is a client-only flag the
  // server knows nothing about, so writing the response object wholesale would collapse
  // any branch that happens to come back in a refetched batch.
  action.payload.newLocations.forEach((newLocation) => {
    const previous = locationById.get(newLocation.id);
    locationById.set(
      newLocation.id,
      previous ? { ...newLocation, expanded: previous.expanded } : newLocation,
    );
    const currentChildren = childrenByParentId.get(newLocation.parent_id) || [];
    childrenByParentId.set(newLocation.parent_id, [...currentChildren, newLocation.id]);
  });

  return { ...state, locationById, childrenByParentId };
};

/** Toggle the expanded state of a location. */
const setLocationExpandedAction = (state: LocationsState, action: SetLocationExpandedAction) => {
  const locationById = new Map(state.locationById);
  const currentLocation = locationById.get(action.payload.locationId);
  if (currentLocation) {
    currentLocation.expanded = action.payload.expanded ?? !currentLocation.expanded;
  }
  return { ...state, locationById };
};

const toggleAllLocationsSelectionAction = (state: LocationsState) => {
  const newState = { ...state };
  newState.selectedLocations.clear();
  newState.allLocationsSelected = !newState.allLocationsSelected;
  return newState;
};

/** Remove a location from the state. Also removes it from its parent's children list. */
const deleteLocationAction = (state: LocationsState, action: DeleteLocationAction) => {
  const locationById = new Map(state.locationById);
  const childrenByParentId = new Map(state.childrenByParentId);
  const selectedLocations = new Map(state.selectedLocations);

  const locationToDelete = locationById.get(action.payload.locationId);
  if (!locationToDelete) return state;

  // Remove from locationById
  locationById.delete(action.payload.locationId);

  // Remove from parent's children list
  const parentId = locationToDelete.parent_id;
  if (parentId !== null) {
    const siblings = childrenByParentId.get(parentId) || [];
    const updatedSiblings = siblings.filter((id) => id !== action.payload.locationId);
    childrenByParentId.set(parentId, updatedSiblings);

    // Update the parent's children count if there are no more siblings
    const parent = locationById.get(parentId);
    if (parent && updatedSiblings.length === 0 && !parent?.has_more_children) {
      parent.children_count = 0;
    }
  }

  // Remove from selectedLocations if present
  selectedLocations.delete(action.payload.locationId);

  // Recursively delete all descendants
  const deleteDescendants = (parentId: number) => {
    const childrenIds = childrenByParentId.get(parentId) || [];
    childrenIds.forEach((childId) => {
      deleteDescendants(childId);
      locationById.delete(childId);
      selectedLocations.delete(childId);
    });
    childrenByParentId.delete(parentId);
  };

  deleteDescendants(action.payload.locationId);

  return { ...state, locationById, childrenByParentId, selectedLocations };
};

const locationsReducer = (state: LocationsState, action: LocationsAction) => {
  switch (action.type) {
    case 'reset_location_state':
      return { ...state, ...action.payload };
    case 'set_location_expanded': {
      return setLocationExpandedAction(state, action);
    }
    case 'update_location_children': {
      return updateLocationChildrenAction(state, action);
    }
    case 'update_location_selection': {
      return toggleLocationSelectionAction(state, action);
    }
    case 'update_single_location_selection': {
      const isCurrentlySelected = state.selectedLocations.get(action.payload) === 'selected';

      if (isCurrentlySelected) {
        return {
          ...state,
          selectedLocations: new Map<number, SelectedStatus>(),
        };
      } else {
        return {
          ...state,
          selectedLocations: new Map<number, SelectedStatus>([[action.payload, 'selected']]),
        };
      }
    }
    case 'toggle_all_locations_selection': {
      return toggleAllLocationsSelectionAction(state);
    }
    case 'delete_location': {
      return deleteLocationAction(state, action);
    }
    default:
      return state;
  }
};

export const useLocationsReducer = (
  initialSelectedLocations?: Record<string, SelectedStatus>,
  initialAllLocationsSelected?: boolean,
) => {
  return useReducer(
    locationsReducer,
    getDefaultLocationsState(initialSelectedLocations, initialAllLocationsSelected),
  );
};
